// ────────────────────────────────────────────────────────────────
//  Board status diagnosis
//
//  Turns the raw signals a board exposes (broker presence, telemetry
//  freshness, enable flag, duplicate-code quarantine, open alerts) into a
//  human explanation of WHY a board reads offline / no-data. Powers the
//  click-to-diagnose popup so an operator doesn't have to guess.
//
//  Built on the canonical boardStatus so the popup never disagrees with the
//  badge next to it.
// ────────────────────────────────────────────────────────────────
import {
  boardStatusOf,
  BOARD_STATUS_META,
  mqttDisplayState,
  MQTT_STATE_META,
  type BoardStatus,
  type MqttState,
} from './boardStatus';

export type DiagnosisSeverity = 'ok' | 'info' | 'warn' | 'error';

export interface DiagnosisAlert {
  code: string;
  message: string;
  severity?: string;
}

export interface BoardDiagnosisInput {
  isActive: boolean;
  brokerConnected?: boolean | null;
  lastSeenAt?: string | null;
  /** Freshest in-memory telemetry point (ms) — folded into last-activity. */
  extraActivityMs?: number | null;
  pendingDeviceUid?: { deviceUid: string; ipAddress?: string } | null;
  /** Open alerts already scoped to THIS board. */
  alerts?: DiagnosisAlert[];
}

export interface BoardDiagnosis {
  status: BoardStatus;
  statusLabel: string;
  statusColor: string;
  severity: DiagnosisSeverity;
  headline: string;
  reasons: string[];
  lastActivityMs: number | null;
  mqttState: MqttState;
}

/** Human "X ago" in Thai from a past epoch ms. */
export function ageText(ms: number | null, now: number = Date.now()): string {
  if (ms == null) return 'ยังไม่เคยส่งข้อมูล';
  const s = Math.max(0, Math.round((now - ms) / 1000));
  if (s < 60) return `${s} วินาที`;
  const m = Math.round(s / 60);
  if (m < 60) return `${m} นาที`;
  const h = Math.floor(m / 60);
  return `${h} ชม.${m % 60 ? ` ${m % 60} นาที` : ''}`;
}

export function diagnoseBoard(
  input: BoardDiagnosisInput,
  now: number = Date.now(),
): BoardDiagnosis {
  const status = boardStatusOf(
    { brokerConnected: input.brokerConnected, lastSeenAt: input.lastSeenAt },
    input.extraActivityMs,
    now,
  );
  const meta = BOARD_STATUS_META[status];
  const base = input.lastSeenAt ? new Date(input.lastSeenAt).getTime() : 0;
  const lastActivityMs = Math.max(base, input.extraActivityMs ?? 0) || null;
  const age = ageText(lastActivityMs, now);
  // Single source of truth for the MQTT link — same helper every page uses, so
  // the popup can't disagree with the badge. 'connected' only when the broker
  // says so OR telemetry is fresh; a stale board is 'unknown', never a guessed
  // "connected" from an old self-report.
  const mqttState = mqttDisplayState(input.brokerConnected, lastActivityMs, now);

  const alerts = input.alerts ?? [];
  const reasons: string[] = [];
  let severity: DiagnosisSeverity = 'ok';
  let headline = 'ทำงานปกติ';

  if (!input.isActive) {
    severity = 'info';
    headline = 'บอร์ดถูกปิดใช้งาน';
    reasons.push(
      'บอร์ดนี้ถูกตั้งเป็น "ปิดใช้งาน" ใน monitor-web → ระบบไม่เก็บและไม่แสดงข้อมูลจากบอร์ดนี้ (เปิดใช้งานเพื่อให้กลับมาเก็บข้อมูล)',
    );
  } else if (input.pendingDeviceUid) {
    severity = 'warn';
    headline = 'พบอุปกรณ์ใช้รหัสบอร์ดซ้ำ';
    reasons.push(
      `มีอุปกรณ์ ${input.pendingDeviceUid.deviceUid}` +
        (input.pendingDeviceUid.ipAddress ? ` (IP ${input.pendingDeviceUid.ipAddress})` : '') +
        ' ส่งข้อมูลมาด้วยรหัสเดียวกัน — ข้อมูลถูกกักไว้ทั้งหมดจนกว่าแอดมินจะกดยอมรับ',
    );
  } else if (status === 'online') {
    const noSig = alerts.filter((a) => a.code === 'sensor_no_signal');
    if (noSig.length) {
      severity = 'warn';
      headline = 'ออนไลน์ แต่มีเซนเซอร์ไม่มีสัญญาณ';
    } else {
      severity = 'ok';
      headline = 'ทำงานปกติ';
      reasons.push(`ส่งข้อมูลล่าสุดเมื่อ ${age}ที่แล้ว · MQTT เชื่อมต่อ`);
    }
  } else if (status === 'never') {
    severity = 'error';
    headline = 'ยังไม่เคยส่งข้อมูล';
    reasons.push('บอร์ดนี้ยังไม่เคยส่ง telemetry เข้ามาเลย — ตรวจสอบการตั้งค่า/การเชื่อมต่อครั้งแรก');
  } else if (mqttState === 'connected') {
    // Broker says the socket is alive but publishing stopped — the easy-to-miss
    // stall. We can only assert this when MQTT is AUTHORITATIVELY connected.
    severity = 'error';
    headline = 'MQTT เชื่อมต่อ แต่ไม่ส่งข้อมูล (telemetry stall)';
    reasons.push(
      `MQTT socket ยังเชื่อมต่ออยู่ แต่บอร์ดหยุดส่ง telemetry มา ${age} → บอร์ดค้างฝั่งส่งข้อมูล (ไม่ใช่เน็ตหลุด)`,
    );
    reasons.push(
      'แนะนำ: รีบูตบอร์ด (ถ้าเข้าถึงได้) — ต้นเหตุนี้จะถูกแก้ถาวรด้วย firmware telemetry-liveness watchdog',
    );
  } else if (mqttState === 'disconnected') {
    severity = 'error';
    headline = 'ออฟไลน์ — MQTT หลุด';
    reasons.push(`ไม่ได้รับข้อมูลมา ${age} และ broker รายงานว่า MQTT หลุด → บอร์ด / เน็ตเวิร์ก / ไฟ อาจหลุด`);
  } else {
    // brokerConnected unknown (e.g. no EMQX event since backend start) AND
    // telemetry stale — we honestly can't tell stall from a dead link.
    severity = 'error';
    headline = 'ออฟไลน์ — ไม่ได้รับข้อมูล';
    reasons.push(
      `ไม่ได้รับข้อมูลมา ${age} · สถานะ MQTT: ไม่ทราบ (broker ยังไม่รายงานหลัง backend เริ่มใหม่)`,
    );
    reasons.push(
      'ถ้า broker ยังเห็นบอร์ด connected = บอร์ดค้างฝั่งส่ง (telemetry stall); ถ้าไม่เห็น = เน็ต/ไฟหลุดจริง',
    );
  }

  // Fold in any open alerts not already spelled out.
  for (const a of alerts) {
    if (!reasons.includes(a.message)) reasons.push(a.message);
  }

  return {
    status,
    statusLabel: meta.label,
    statusColor: meta.color,
    severity,
    headline,
    reasons,
    lastActivityMs,
    mqttState,
  };
}

/** Thai label + colour for the MQTT line, re-exported for callers rendering a
 *  standalone chip. */
export function mqttStateMeta(state: MqttState) {
  return MQTT_STATE_META[state];
}
